// Workspace — the right-side dock that holds the active selection card
// plus any pinned cards. Minimized cards collapse to a horizontal pill
// strip along the bottom of the map.
//
// Decisions:
//   - The dispatcher's currently-selected entity ALWAYS shows expanded
//     (you can't minimize the focus). It sits at the top of the stack.
//   - Pinned-but-not-selected entities follow underneath, in pin order.
//     Each can be minimized to a pill in the bottom dock.
//   - The selected entity is always visible — pinning it adds a pin
//     icon to its card so the card persists when selection moves on.
//   - "Reset workspace" button (in the dock) clears all pinned + minimized.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import GateDetailCard, { resolveDetail as resolveGateDetail } from './GateDetailCard';
import DriverDetailCard, { resolveDriverDetail } from './DriverDetailCard';
import { useIntel } from './IntelligenceContext';
import { C } from '../styles';

// Decide which card component renders for an entity, and resolve the
// underlying detail (used by minimized pills too).
function resolveAny(entity) {
  if (!entity) return null;
  if (entity.kind === 'driver') return { Card: DriverDetailCard, detail: resolveDriverDetail(entity) };
  return { Card: GateDetailCard, detail: resolveGateDetail(entity) };
}

const entityKey = (e) => e ? `${e.kind}:${e.id}` : null;

function Workspace() {
  const intel = useIntel();
  const {
    selected, pinnedEntities,
    isMinimized, isPinned,
    toggleMinimize, togglePin, clearWorkspace,
  } = intel;

  // Drag state for the right-column card stack. Null until the user
  // grabs it; once dragged, switches from the default top-right pin to
  // explicit top/left coordinates inside the parent.
  const [stackPos, setStackPos] = useState(null);
  const stackDragRef = useRef(null);
  const stackRef = useRef(null);
  const onStackDragStart = (e) => {
    if (!stackRef.current) return;
    const rect = stackRef.current.getBoundingClientRect();
    stackDragRef.current = {
      grabX: e.clientX, grabY: e.clientY,
      originX: rect.left, originY: rect.top,
      width: rect.width, height: rect.height,
    };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!stackDragRef.current) return;
      const d = stackDragRef.current;
      const parent = stackRef.current?.offsetParent;
      const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
      const dx = e.clientX - d.grabX;
      const dy = e.clientY - d.grabY;
      let left = (d.originX + dx) - parentRect.left;
      let top  = (d.originY + dy) - parentRect.top;
      if (parent) {
        left = Math.max(4, Math.min(left, parent.clientWidth  - d.width  - 4));
        top  = Math.max(4, Math.min(top,  parent.clientHeight - d.height - 4));
      }
      setStackPos({ top, left });
    };
    const onUp = () => { stackDragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Build the list of entities to render as expanded cards in the right
  // column. Order: selected first (if it has a card), then pinned ones
  // (excluding the selected so we don't double-render) that aren't
  // minimized.
  const selectedKey = entityKey(selected);
  const expandedEntities = [];
  if (selected && resolveAny(selected)?.detail) {
    expandedEntities.push(selected);
  }
  pinnedEntities.forEach(p => {
    const k = entityKey(p);
    if (k === selectedKey) return; // already added as selected
    if (isMinimized(p)) return;     // collapsed → goes in the bottom dock
    if (!resolveAny(p)?.detail) return;
    expandedEntities.push(p);
  });

  // Minimized pills (pinned + minimized)
  const minimizedEntities = pinnedEntities.filter(p => isMinimized(p));

  const hasAnyWorkspace = pinnedEntities.length > 0 || expandedEntities.length > 0;

  return (
    <>
      {/* Floating, draggable card stack. Defaults to top-right; once
          the dispatcher grabs the drag handle the card can be moved
          anywhere inside the parent container. */}
      {expandedEntities.length > 0 && (
        <Box
          ref={stackRef}
          data-intel-overlay
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            ...(stackPos
              ? { top: stackPos.top, left: stackPos.left }
              : { top: 70, right: 12, bottom: minimizedEntities.length > 0 ? 64 : 12 }),
            width: 380,
            zIndex: 15,
            display: 'flex', flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Drag handle — slim strip at the top of the stack. Grab here
              to move the cards. Buttons inside cards still work because
              they're outside this handle. */}
          <Box
            onMouseDown={onStackDragStart}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 0.5, py: 0.4,
              cursor: 'move',
              userSelect: 'none',
              backgroundColor: 'rgba(30, 30, 30, 0.7)',
              border: `1px solid ${C.border}`,
              borderRadius: 1,
              color: C.textMuted,
              '&:hover': { color: C.textSecondary, backgroundColor: 'rgba(40, 40, 40, 0.85)' },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Drag to move
            </Typography>
          </Box>
          {expandedEntities.map((e) => {
            const { Card } = resolveAny(e);
            return (
              <Card
                key={entityKey(e)}
                entity={e}
                isSelected={entityKey(e) === selectedKey}
              />
            );
          })}
        </Box>
      )}

      {/* Bottom dock — minimized cards as pills + reset workspace */}
      {(minimizedEntities.length > 0 || pinnedEntities.length > 0) && (
        <Box
          data-intel-overlay
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            bottom: 12, left: 12, right: 12,
            zIndex: 14,
            display: 'flex', alignItems: 'center', gap: 0.75,
            pointerEvents: 'none',  // children re-enable
          }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap',
            pointerEvents: 'auto',
          }}>
            {minimizedEntities.map(e => (
              <MinimizedPill
                key={entityKey(e)}
                entity={e}
                onExpand={() => toggleMinimize(e)}
                onClose={() => togglePin(e)}
              />
            ))}
          </Box>
          <Box sx={{ flex: 1 }} />
          {hasAnyWorkspace && (
            <Box sx={{ pointerEvents: 'auto' }}>
              <Tooltip title="Clear pinned cards">
                <IconButton
                  size="small"
                  onClick={clearWorkspace}
                  sx={{
                    backgroundColor: 'rgba(15, 18, 28, 0.9)',
                    border: `1px solid ${C.border}`,
                    borderRadius: 1,
                    color: C.textMuted,
                    px: 1, py: 0.5,
                    '&:hover': { color: '#f44336', borderColor: '#FF6B6B55' },
                  }}
                >
                  <RestartAltIcon sx={{ fontSize: 14, mr: 0.4 }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                    Reset
                  </Typography>
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}

// A minimized card sits as a pill at the bottom dock.
function MinimizedPill({ entity, onExpand, onClose }) {
  const resolved = resolveAny(entity);
  if (!resolved?.detail) return null;
  const isDriver = entity.kind === 'driver';
  const detail = resolved.detail;

  // Visual badge content + accent color differ by entity kind
  let badgeContent, badgeBg, badgeFg, title, subtitle, issueCount, hasRed;
  if (isDriver) {
    badgeContent = (detail.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('');
    const meta = {
      on_shift: '#94A3B8', in_run: '#00BCD4', at_gate: '#F9B612',
      on_break: '#9C27B0', on_lunch: '#EC4899',
    };
    badgeBg = meta[detail.status] || '#94A3B8';
    badgeFg = '#fff';
    title = detail.name;
    subtitle = `#${detail.badge} · ${(detail.status || '').replace('_', ' ')}`;
    issueCount = detail.issues?.length || 0;
    hasRed = detail.issues?.some(i => i.severity === 'red');
  } else {
    badgeContent = detail.gateNum;
    badgeBg = detail.state === 'parked'   ? '#4CAF50'
            : detail.state === 'arriving' ? '#2196F3'
            : detail.state === 'pushing'  ? '#F9B612'
            : '#94A3B8';
    badgeFg = detail.state === 'pushing' ? '#000' : '#fff';
    title = detail.flightNumber;
    subtitle = `${detail.bagPct}% · ${detail.connections.filter(c => c.isHot).length} hot`;
    issueCount = detail.issues?.length || 0;
    hasRed = detail.issues?.some(i => i.severity === 'red');
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      backgroundColor: 'rgba(15, 18, 28, 0.95)',
      border: `1px solid ${C.border}`,
      borderRadius: 1,
      pl: 0.4, pr: 0.4,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      minHeight: 32,
    }}>
      <Box
        onClick={onExpand}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.6,
          cursor: 'pointer',
          pl: 0.4, pr: 0.6, py: 0.4,
          borderRadius: 0.5,
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
        }}
      >
        <Box sx={{
          width: 22, height: 22,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: badgeBg,
          color: badgeFg,
          fontWeight: 700, fontSize: isDriver ? '0.6rem' : '0.65rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          borderRadius: isDriver ? '50%' : 0.4,
          letterSpacing: 0.3,
        }}>
          {badgeContent}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{
            fontSize: '0.7rem', color: C.textPrimary, fontWeight: 700, lineHeight: 1.1,
            fontFamily: isDriver ? 'inherit' : 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            {issueCount > 0 && (
              <Box sx={{
                fontSize: '0.55rem', fontWeight: 800, letterSpacing: 0.4,
                color: hasRed ? '#FF0000' : '#F9B612',
                px: 0.4, borderRadius: 0.3,
                backgroundColor: hasRed ? 'rgba(255, 0, 0, 0.15)' : 'rgba(249, 182, 18, 0.15)',
              }}>
                {issueCount} issue{issueCount === 1 ? '' : 's'}
              </Box>
            )}
            <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, lineHeight: 1 }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <KeyboardArrowUpIcon sx={{ fontSize: 14, color: C.textMuted }} />
      </Box>
      <Tooltip title="Unpin">
        <IconButton size="small" onClick={onClose} sx={{ color: C.textMuted, p: 0.25, '&:hover': { color: '#f44336' } }}>
          <CloseIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default Workspace;
